import { Accordion, Box, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

const HEADER_TEST_ID = 'accordion-header';
const BODY_TEST_ID = 'accordion-body';
const CARD_VARIANT = 'enclosed';
const FILLED_HEADER = 'filled';
const HEADER_BACKGROUND = 'bg.muted';
const BODY_BACKGROUND = 'bg';
const HEADER_BORDER_COLOUR = 'border';
const CARD_PADDING = 'var(--accordion-padding-x)';

type VAccordionCardHeader = 'filled' | 'plain';

type VAccordionCardProps = {
  title: string;
  children: ReactNode;
  value?: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  header?: VAccordionCardHeader;
  collapsible?: boolean;
  bodyPadding?: boolean;
  indicatorPosition?: 'start' | 'end' | 'none';
  prefixAction?: ReactNode;
  suffixAction?: ReactNode;
  truncateTitle?: boolean;
};

const VAccordionCard = ({
  title,
  children,
  value = title,
  defaultOpen = false,
  open,
  onOpenChange,
  header = FILLED_HEADER,
  collapsible = true,
  bodyPadding = true,
  indicatorPosition = 'end',
  prefixAction,
  suffixAction,
  truncateTitle = false,
}: VAccordionCardProps) => {
  const isFilled = header === FILLED_HEADER;
  const openValue = open ? [value] : [];

  return (
    <Accordion.Root
      variant={CARD_VARIANT}
      collapsible={collapsible}
      defaultValue={defaultOpen ? [value] : undefined}
      value={open === undefined ? undefined : openValue}
      onValueChange={(details) => onOpenChange?.(details.value.includes(value))}
    >
      <Accordion.Item value={value}>
        <Box
          data-testid={HEADER_TEST_ID}
          display="flex"
          alignItems="center"
          gap="2"
          bg={isFilled ? HEADER_BACKGROUND : BODY_BACKGROUND}
          borderBottomWidth={isFilled ? '1px' : undefined}
          borderBottomColor={isFilled ? HEADER_BORDER_COLOUR : undefined}
          pe={CARD_PADDING}
        >
          <Accordion.ItemTrigger display="flex" alignItems="center" minW="0" py={CARD_PADDING}>
            {prefixAction}

            {indicatorPosition === 'start' && <Accordion.ItemIndicator />}
            <Text flex="1" minW="0" truncate={truncateTitle} textAlign="left" fontWeight={'bold'}>
              {title}
            </Text>
            {indicatorPosition === 'end' && <Accordion.ItemIndicator />}
          </Accordion.ItemTrigger>
          {suffixAction}
        </Box>

        <Accordion.ItemContent bg={BODY_BACKGROUND} px={bodyPadding ? undefined : '0'}>
          <Accordion.ItemBody data-testid={BODY_TEST_ID} padding={bodyPadding ? undefined : '0'}>
            {children}
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  );
};

export default VAccordionCard;
